/**
 * §11.6 ribbon trails: width per entity radius, a capped segment count, lifetime banded
 * by source speed. Two merged vertex buffers — one for player and friendly projectiles,
 * one for elite and swift enemies — so the whole layer costs 2 draw calls (§14.2).
 *
 * AMD-39 splits the layer into two profiles, {@link TRAIL_SPARK} and {@link TRAIL_FLOW}.
 * Projectiles keep the AMD-7 R2a ribbon exactly; the player and the swarm get one ten
 * times longer, a third as wide and half as bright. AMD-43 then takes `flow`'s ink down
 * by a further 5×, all of it out of alpha: these ribbons are visual richness, not the
 * motion signal AMD-39 built them as. `spark` is untouched — a projectile's streak is
 * weapon feedback and answers to §11.6, not to this note.
 *
 * AMD-6 D6 resamples at RENDER rate: a point is appended every time the source has moved
 * one reference pixel, and below that the head is moved rather than duplicated. No
 * gameplay stream is read and nothing here allocates after boot.
 */

import * as THREE from 'three';
import type { RngStream } from '../core/rng';
import { nearestX, nearestY, wrapDeltaX, wrapDeltaY } from '../sim/wrap';
import { LAYER_TRAIL } from './instanced';
import { LINEAR } from './palette';
import { quality } from './quality';
import { TRAIL_FRAG, TRAIL_VERT } from './shaders/instanced';
import { unwrappedWarpUniforms } from './warp';

/**
 * Segment budget. `quality.trailSegments` may shorten it, never lengthen it.
 *
 * AMD-39 raised this from the §11.6 24 because the ribbon it has to describe grew ten
 * times longer. 24 chords across a 22 u enemy line is a 33 px chord, and across the
 * player's 26 u line a 39 px one — enough to read as a polygon on a circle-strafe. 48
 * halves that, and the extra segments are only *spent* by the things that need them: a
 * ribbon appends on an interval of `life / (segments - 2)`, so a 60 u/s bolt still lays
 * three points in its 50 ms and costs exactly what it did before.
 */
export const MAX_SEGMENTS = 48;
/**
 * AMD-6 D6: minimum travel before a new point is appended, in world units. Was 0.14 —
 * five reference pixels — which let a slow orbital lay one point every four frames and
 * read as a polyline. At one pixel, anything moving faster than 1.8 u/s samples every
 * rendered frame.
 */
const MIN_STEP = 0.028;

/**
 * AMD-39 — two ribbon profiles, because a ribbon that is *carried* and a ribbon that is
 * *left behind* are different pictures and want different ink.
 *
 * A projectile leaves its origin behind, so its ribbon points at somewhere the ship no
 * longer is: `spark` is the AMD-7 R2a tuning, held to 3 u by `TRAIL_MAX_LEN`, and this
 * change does not touch a pixel of it. A player or an enemy carries its ribbon, so a long
 * one traces a path: `flow` is ten times longer, a third the width and half the alpha,
 * with the taper moved from linear onto a squared ramp so the far end is felt rather than
 * seen.
 *
 * The arithmetic that sets `flow`'s numbers is the integrated ink, `length × width ×
 * alpha`, which is the quantity AMD-7 R2a's own note holds "within 1.3× of AMD-6's". Mean
 * `alpha × width` over the ribbon is 0.328 for `spark` and 0.084 for `flow`; against a
 * ten-fold length and a 0.33× width that lands `flow` at **0.86×** the ink of the ribbon
 * it replaces. Ten times the line, and slightly less of it — which is what the measured
 * background luminance did too: 17:00 max density fell from p90 0.0585 to 0.0578 at
 * 1920×1080 and from 0.0733 to 0.0662 at 390×844.
 */
export interface TrailProfile {
  /** Ribbon half-width per unit of source radius. §11.6's figure is 0.3. */
  readonly widthFactor: number;
  /** Floor on the head half-width, in world units, so a tiny source still draws a thread. */
  readonly minHalfWidth: number;
  /** Half-width at the oldest end, as a fraction of the head's. */
  readonly tailWidthFrac: number;
  /** Alpha at the head, before the body ramp. */
  readonly headAlpha: number;
  /** Fraction of the ribbon the tail ramp is spread across. */
  readonly fadeFrac: number;
  /** Squares the tail ramp, which is what turns a fade into a vanish. */
  readonly fadeSquared: boolean;
  /** Alpha at the tail end of the body ramp, as a fraction of the head's. */
  readonly bodyFloor: number;
}

/** Index into {@link TRAIL_PROFILES}. Friendly projectiles — AMD-7 R2a, unchanged. */
export const TRAIL_SPARK = 0;
/** Index into {@link TRAIL_PROFILES}. The player and the swarm — AMD-39's motion field. */
export const TRAIL_FLOW = 1;

export const TRAIL_PROFILES: readonly TrailProfile[] = [
  // AMD-7 R2a, stated in reference pixels (36 px per world unit at the §11 framing):
  // head 4 px full width, tail 1 px, alpha 0.7 at the head reaching zero across the
  // oldest quarter. A ribbon that keeps one width reads as rope; one that collapses to
  // nothing loses the thread.
  {
    widthFactor: 0.3,
    minHalfWidth: 2 / 36,
    tailWidthFrac: 0.25,
    headAlpha: 0.7,
    fadeFrac: 0.25,
    fadeSquared: false,
    bodyFloor: 0.45,
  },
  // AMD-39. Width: 0.2 × radius, i.e. 4.0 px on a driftfin and 5.0 px on the ship, down
  // from 11.9 and 15.1 — a hairline, which is what "thin" has to mean once the line is
  // 770 px long. The 2 px floor is one reference pixel of half-width: below that an
  // un-antialiased quad drops out of the raster in dashes.
  //
  // AMD-43 takes the head alpha to 0.068 — exactly a fifth of AMD-39's 0.34, and the
  // whole of the ribbon's ink with it, because the shape of the ramp is unchanged and the
  // width is not touched.
  //
  // Alpha is the only lever that could pay this. Width is already 0.10 × radius: 5.0 px on
  // the ship and 4.0 px on a driftfin at 1080p, which on a 390 px phone is 2.8 px, and one
  // more halving would put the quad under a device pixel and turn a continuous ribbon into
  // a dashed one. Length was ruled out by the ask itself — the ten-fold line is what the
  // last round bought and the note keeps it.
  //
  // Where 0.068 lands, and how "felt, not read" was judged. Alpha is `0.068 · t² ·
  // (0.35 + 0.65 t)` and the layer is additive against a background the AMD-6 cap holds at
  // 0.06, so the head of the ribbon contributes 0.109 of linear light on top of it — a
  // little under 2× the floor, in the 40 px directly behind the ship, falling to 0.021 at
  // the halfway mark and to 0.0045 at a quarter of the length. 1/255 is 0.0039. So the
  // ribbon is above the 8-bit quantisation floor for roughly its first quarter and
  // measurable-but-unresolvable after that: you cannot follow this line to its end, and
  // that is the point. It is texture behind a moving object, not a signal you read.
  //
  // The 3/10 motion score that produced AMD-39 was a finding about a frame with NO
  // velocity information at all. A fifth of the ink still leaves every moving thing with a
  // wake; it stops the wake from being something you look at.
  {
    widthFactor: 0.1,
    minHalfWidth: 1 / 36,
    tailWidthFrac: 0.5,
    headAlpha: 0.068,
    fadeFrac: 1,
    fadeSquared: true,
    bodyFloor: 0.35,
  },
];

/**
 * AMD-39 — how long a ribbon outlives its last report, in seconds.
 *
 * At §11.6 lengths a ribbon evaporated on its own: every point aged past a 0.4 s lifetime
 * and there was nothing left to see. At `flow` lengths the lifetime is up to 3.2 s, so a
 * unit that dies — or drops below {@link SWARM_TRAIL_MIN_SPEED}, or leaves the frame —
 * would hang a 22 u line in the water for three seconds and hold a pool slot for the same
 * three seconds. `IDLE_HOLD` is four frames of grace so a single skipped report does not
 * dim anything; past it the whole ribbon fades and the slot is returned.
 */
const IDLE_HOLD = 0.06;
const IDLE_RELEASE = 0.3;

/** §11.6 lifetime bands, by source speed in u/s. */
export function trailLifetimeForSpeed(speed: number): number {
  if (speed < 15) return 0;
  if (speed < 30) return 0.15;
  if (speed < 45) return 0.25;
  return 0.35;
}

/**
 * AMD-6 D6: player projectiles below the §11.6 15 u/s band had no trail at all, which
 * is what made orbitals and mines read as static stamps. They now get a 140 ms floor —
 * the shortest band the art direction specifies — while every §11.6 band above it is
 * untouched.
 */
export function projectileTrailLifetime(speed: number): number {
  const banded = trailLifetimeForSpeed(speed);
  return banded > 0 ? banded : 0.14;
}

/**
 * AMD-6 D10 / AMD-7 R2c — the swarm ribbon, so a formation has internal motion. Elites
 * and `affix.swift` keep their longer §11.6 tell on top of it.
 *
 * Stated as a DRAWN LENGTH, not as an age. The 60–180 ms band this replaces was an age,
 * and an age is the wrong unit for a ribbon: what the eye measures is how far back the
 * line reaches, and `length = speed × lifetime` meant the slowest archetypes — which is
 * most of the roster, `move_speed` 1.1 to 5.2 — drew 2 to 14 px of ribbon and read as
 * nothing at all. Below 3 u/s they drew nothing literally. The same substitution went the
 * other way for projectiles (`TRAIL_MAX_LEN`), where an age let a 60 u/s bolt stand up a
 * bar; enemies are twenty times slower and had the opposite failure.
 *
 * So a target length is picked from the unit's own speed and the lifetime falls out of it:
 *
 *  - the target ramps `SWARM_LEN_MIN` → `SWARM_LEN_MAX` across the 2–11 u/s band the
 *    swarm occupies once §8.10's aggression ramp is running. This is R2c's motion field,
 *    restated in the unit the field is actually read in: a 32 px line at the back of a
 *    formation against a 79 px line at its leading edge, instead of 4 px against 12 px.
 *  - `SWARM_LIFE_MAX` caps how long a ribbon may be held. A crawler at 1.1 u/s cannot
 *    reach `SWARM_LEN_MIN` without towing a stale line that no longer describes where it
 *    has been, so it draws a shorter one and keeps the record honest.
 *  - `SWARM_LIFE_MIN` is the far end of the same trade for anything driven past 14 u/s.
 *
 * AMD-39 multiplies the length target by ten and moves the two lifetime clamps with it.
 * 0.9–2.2 u was 32–79 px, which at forty-four units read as a sticker sheet of marks; 9–22
 * u is 324–792 px, which reads as a field of flow lines you can see the shape of the flock
 * in. The clamps are what stop that from becoming a lie: the target is a *maximum* the
 * unit has to be fast enough to earn, and `SWARM_LIFE_MAX` decides how much history a
 * ribbon is allowed to describe. At 3.2 s the fast half of the roster (driftfin 5.2,
 * glimmerfry 6.8, dart-eel 10.5 u/s) reaches its full target and lands on a true 10×; the
 * slow end (brood-bell 2.0, colonial-curtain 2.5) is held to 6.4–8.0 u and lands nearer
 * 8×, which is the honest answer — a crawler has not *been* 22 u away in any span of time
 * a ribbon should still be claiming.
 */
const SWARM_LEN_MIN = 9;
/** The longest line an ordinary swarm unit can draw, before the `affix.swift` 1.5×. */
export const SWARM_LEN_MAX = 22;
const SWARM_SPEED_LO = 2;
const SWARM_SPEED_HI = 11;
const SWARM_LIFE_MIN = 0.3;
const SWARM_LIFE_MAX = 3.2;

/**
 * @param lengthMult class elongation (`affix.swift`, dart-eel). Applied to the length
 * target rather than to the returned lifetime, so the staleness clamp stays authoritative
 * and a slow swift unit does not tow four and a half seconds of stale line.
 */
export function swarmTrailLifetime(speed: number, lengthMult = 1): number {
  if (speed <= 0) return 0;
  const t = Math.max(0, Math.min(1, (speed - SWARM_SPEED_LO) / (SWARM_SPEED_HI - SWARM_SPEED_LO)));
  const life = ((SWARM_LEN_MIN + (SWARM_LEN_MAX - SWARM_LEN_MIN) * t) * lengthMult) / speed;
  return life < SWARM_LIFE_MIN ? SWARM_LIFE_MIN : life > SWARM_LIFE_MAX ? SWARM_LIFE_MAX : life;
}

/**
 * AMD-39 — the ship's own length budget, which it has never had.
 *
 * The player rides the friendly buffer, so it inherited `projectileTrailLifetime`: a flat
 * 0.14 s floor, which at the 8 u/s `player.move_speed` drew 1.1 u — 40 px — and made the
 * one object the whole frame is composed around the only moving thing that left no record
 * of where it had been. It is now sized the same way the swarm is, by drawn length, and
 * for the same reason: what the eye measures is how far back the line reaches.
 *
 * 26 u is 936 px, close to half the visible width at the 68 u framing, and it is spent
 * almost entirely on the invisible end of the taper — the `flow` profile puts the ship's
 * ribbon under 0.05 alpha past its halfway mark, so AMD-7 R3's "the player is the
 * brightest pixel" survives a line this long. `PLAYER_TRAIL_LIFE_MAX` is the same
 * staleness argument as the swarm's, one tick longer: below 8 u/s the ship draws
 * `speed × 3.2` rather than a stale 26.
 *
 * `TRAIL_MAX_LEN` in `renderer.ts` is deliberately not applied here. It exists because a
 * bolt leaves its origin behind and a long ribbon then points at a muzzle the ship has
 * left; the ship carries its ribbon, so the same length traces a path instead of lying
 * about one.
 */
export const PLAYER_TRAIL_LEN = 26;
const PLAYER_LIFE_MIN = 0.25;
const PLAYER_LIFE_MAX = 3.2;

export function playerTrailLifetime(speed: number): number {
  if (speed <= 0) return 0;
  const life = PLAYER_TRAIL_LEN / speed;
  return life < PLAYER_LIFE_MIN ? PLAYER_LIFE_MIN : life > PLAYER_LIFE_MAX ? PLAYER_LIFE_MAX : life;
}

/**
 * Below this the unit is not travelling, it is drifting or holding station, and a ribbon
 * would be a smear rather than a record. Two archetypes declare `move_speed: 0` outright.
 *
 * This is also what keeps the 256-ribbon pool spent on things that are moving: a slot is
 * claimed on the first `track` call and held until the ribbon ages out, so a station
 * keeper that never appends a second point would deny a slot to a unit crossing the frame.
 */
export const SWARM_TRAIL_MIN_SPEED = 0.5;

/** §03.4 dash: a custom 0.30 s band at a 32 u/s burst. */
export const DASH_TRAIL_LIFETIME = 0.3;
/** §7.2.3 / §7.5: `affix.swift` and dart-eel elongate their class band by 1.5×. */
export const SWIFT_TRAIL_MULT = 1.5;

class TrailBuffer {
  readonly mesh: THREE.Mesh;
  readonly capacity: number;

  private readonly geometry = new THREE.BufferGeometry();
  private readonly material: THREE.RawShaderMaterial;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  private readonly posAttr: THREE.BufferAttribute;
  private readonly colAttr: THREE.BufferAttribute;

  private readonly px: Float32Array;
  private readonly py: Float32Array;
  private readonly pt: Float32Array;
  private readonly head: Int32Array;
  private readonly len: Int32Array;
  private readonly owner: Int32Array;
  private readonly color: Int32Array;
  private readonly profile: Int32Array;
  private readonly alphaMul: Float32Array;
  private readonly halfWidth: Float32Array;
  private readonly lifetime: Float32Array;
  private readonly touched: Float32Array;
  private readonly slotOf: Int32Array;
  /** Reverse map, so an expired ribbon can free itself without scanning `slotOf`. */
  private readonly ownerSlotOf: Int32Array;
  private readonly free: Int32Array;
  private freeTop: number;

  private vertexCount = 0;

  constructor(capacity: number, ownerCapacity: number, renderOrder: number, name: string) {
    this.capacity = capacity;
    this.px = new Float32Array(capacity * MAX_SEGMENTS);
    this.py = new Float32Array(capacity * MAX_SEGMENTS);
    this.pt = new Float32Array(capacity * MAX_SEGMENTS);
    this.head = new Int32Array(capacity);
    this.len = new Int32Array(capacity);
    this.owner = new Int32Array(capacity);
    this.color = new Int32Array(capacity);
    this.profile = new Int32Array(capacity);
    this.alphaMul = new Float32Array(capacity);
    this.halfWidth = new Float32Array(capacity);
    this.lifetime = new Float32Array(capacity);
    this.touched = new Float32Array(capacity);
    this.slotOf = new Int32Array(ownerCapacity);
    this.slotOf.fill(-1);
    this.ownerSlotOf = new Int32Array(capacity);
    this.ownerSlotOf.fill(-1);
    this.free = new Int32Array(capacity);
    for (let i = 0; i < capacity; i++) this.free[i] = capacity - 1 - i;
    this.freeTop = capacity;

    const maxVerts = capacity * (MAX_SEGMENTS - 1) * 6;
    this.positions = new Float32Array(maxVerts * 3);
    this.colors = new Float32Array(maxVerts * 4);
    this.posAttr = new THREE.BufferAttribute(this.positions, 3);
    this.colAttr = new THREE.BufferAttribute(this.colors, 4);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.colAttr.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute('position', this.posAttr);
    this.geometry.setAttribute('color', this.colAttr);
    this.geometry.setDrawRange(0, 0);
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), 1e6);

    this.material = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: TRAIL_VERT,
      fragmentShader: TRAIL_FRAG,
      uniforms: unwrappedWarpUniforms(),
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.SrcAlphaFactor,
      blendDst: THREE.OneFactor,
      side: THREE.DoubleSide,
    });

    this.mesh = new THREE.Mesh(this.geometry, this.material);
    this.mesh.frustumCulled = false;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.renderOrder = renderOrder;
    this.mesh.visible = false;
    this.mesh.name = name;
  }

  /**
   * Appends a point for `ownerSlot`, allocating a ribbon on first sight and resetting it
   * when the pool slot has been recycled to a different entity.
   */
  track(
    ownerSlot: number,
    entityId: number,
    x: number,
    y: number,
    radius: number,
    colorIndex: number,
    profileIndex: number,
    lifetime: number,
    alphaScale: number,
    time: number,
    vfx: RngStream,
  ): void {
    if (lifetime <= 0 || ownerSlot < 0 || ownerSlot >= this.slotOf.length) return;
    let slot = this.slotOf[ownerSlot];
    if (slot >= 0 && this.owner[slot] !== entityId) {
      this.release(ownerSlot);
      slot = -1;
    }
    if (slot < 0) {
      if (this.freeTop === 0) return;
      slot = this.free[--this.freeTop];
      this.slotOf[ownerSlot] = slot;
      this.ownerSlotOf[slot] = ownerSlot;
      this.owner[slot] = entityId;
      this.head[slot] = 0;
      this.len[slot] = 0;
    }
    this.color[slot] = colorIndex;
    this.profile[slot] = profileIndex;
    this.alphaMul[slot] = alphaScale;
    const prof = TRAIL_PROFILES[profileIndex];
    this.halfWidth[slot] = Math.max(radius * prof.widthFactor, prof.minHalfWidth) * quality.trailWidth;
    const life = lifetime * (quality.trailSegments / MAX_SEGMENTS);
    this.lifetime[slot] = life;
    this.touched[slot] = time;

    const base = slot * MAX_SEGMENTS;
    const n = this.len[slot];
    if (n > 0) {
      const last = base + ((this.head[slot] - 1 + MAX_SEGMENTS) % MAX_SEGMENTS);
      // AMD-24: the short arc. The mirror hands this method a coordinate that flips sign
      // on the tick the source crosses the seam, and a raw difference would read that as
      // a 200 u sprint and never take the "move the head" branch again.
      const dx = wrapDeltaX(x - this.px[last]);
      const dy = wrapDeltaY(y - this.py[last]);
      // Two gates, and both of them move the head instead of appending when they hold.
      //  - travel below one reference pixel: two points a fraction of a pixel apart give
      //    a wildly unstable segment normal, which is the herringbone;
      //  - the ribbon's own sampling interval, so the segment budget always spans the
      //    whole lifetime. Without it a 0.25× replay fills the ring four times as fast and the
      //    trail arrives a quarter of its specified length.
      const interval = life / Math.max(2, Math.min(MAX_SEGMENTS, quality.trailSegments) - 2);
      if (dx * dx + dy * dy < MIN_STEP * MIN_STEP || time - this.pt[last] < interval) {
        this.px[last] = x;
        this.py[last] = y;
        return;
      }
    }
    const h = base + this.head[slot];
    // No lateral jitter. It existed to break up a sparse polyline; at render-rate
    // sampling it only fights the ribbon's own direction. `vfx` stays in the signature
    // because the trail API is shared and the stream must not be consumed conditionally.
    this.px[h] = x;
    this.py[h] = y;
    this.pt[h] = time;
    this.head[slot] = (this.head[slot] + 1) % MAX_SEGMENTS;
    if (n < MAX_SEGMENTS) this.len[slot] = n + 1;
  }

  /** Returns a ribbon's slot to the pool. `build` calls this once the owner has been
   * silent for `IDLE_RELEASE` and the ribbon has already faded to nothing; `track` calls
   * it when a pool slot has been recycled to a different entity, where the drop is
   * immediate and correct — the new owner is somewhere else entirely. */
  release(ownerSlot: number): void {
    if (ownerSlot < 0 || ownerSlot >= this.slotOf.length) return;
    const slot = this.slotOf[ownerSlot];
    if (slot < 0) return;
    this.slotOf[ownerSlot] = -1;
    this.ownerSlotOf[slot] = -1;
    this.len[slot] = 0;
    this.head[slot] = 0;
    this.owner[slot] = 0;
    this.free[this.freeTop++] = slot;
  }

  /**
   * Rebuilds the merged vertex buffer. Ribbons untouched this frame expire naturally.
   *
   * AMD-24 — the ribbon is resolved to ONE image of the torus here, on the CPU, and the
   * vertex program is left alone.
   *
   * A ribbon is a strip: its vertices are welded to each other, so wrapping them
   * individually in the shader would split a ribbon that straddles the seam and draw the
   * two halves as one 200 u triangle across the whole frame. Instead each ribbon is
   * unwrapped around its own head (`nearest…`, which is what makes the strip locally
   * Euclidean) and the head is then placed on the camera's side of the cut. Both steps are
   * exact for any ribbon shorter than the half-arena the short arc is unambiguous over:
   * AMD-39 took the longest ribbon from 2.2 u to 33 u against a 100 u budget, which is
   * still three times the margin it needs.
   */
  build(time: number, camX: number, camY: number): void {
    let v = 0;
    const pos = this.positions;
    const col = this.colors;
    const maxVerts = this.capacity * (MAX_SEGMENTS - 1) * 6;
    const segCap = Math.min(MAX_SEGMENTS, Math.max(2, quality.trailSegments));

    for (let slot = 0; slot < this.capacity; slot++) {
      const n = this.len[slot];
      if (n < 1) continue;
      const life = this.lifetime[slot];
      // A ribbon whose owner stopped reporting (died, left the pool, went off screen) is
      // faded out over `IDLE_RELEASE` and the slot reclaimed. AMD-39: this used to be
      // "once its last point has aged out", which at a 0.4 s lifetime *was* a fade and at
      // a 3.2 s one is a corpse's line hanging in the water while the death burst plays
      // over it — and a pool slot held for three seconds at forty-four units a frame.
      const idle = time - this.touched[slot];
      if (life <= 0 || idle > IDLE_RELEASE) {
        this.release(this.ownerSlotOf[slot]);
        continue;
      }
      if (n < 2) continue;
      const base = slot * MAX_SEGMENTS;
      const ci = this.color[slot] * 3;
      const r = LINEAR[ci];
      const g = LINEAR[ci + 1];
      const b = LINEAR[ci + 2];
      const hw = this.halfWidth[slot];
      const prof = TRAIL_PROFILES[this.profile[slot]];
      const wTail = prof.tailWidthFrac;
      const wSpan = 1 - wTail;
      const aFloor = prof.bodyFloor;
      const aSpan = 1 - aFloor;
      const invFade = 1 / prof.fadeFrac;
      const sq = prof.fadeSquared;
      // §11.10 crowd scoping and the idle fade are one scalar by the time they reach the
      // vertex, so a packed unit and a dead one cost the same as an open-field one.
      const idleFade = idle <= IDLE_HOLD ? 1 : 1 - (idle - IDLE_HOLD) / (IDLE_RELEASE - IDLE_HOLD);
      const aHead = prof.headAlpha * this.alphaMul[slot] * idleFade;

      // Walk newest → oldest so the length cap always keeps the freshest segments.
      const first = (this.head[slot] - 1 + MAX_SEGMENTS) % MAX_SEGMENTS;
      const steps = Math.min(n, segCap) - 1;
      // The ribbon's own frame: its head, moved to the image nearest the camera. Every
      // point below is then placed relative to that head, never to the raw coordinate.
      const headX = nearestX(camX, this.px[base + first]);
      const headY = nearestY(camY, this.py[base + first]);
      for (let s = 0; s < steps; s++) {
        const iA = base + ((first - s + MAX_SEGMENTS) % MAX_SEGMENTS);
        const iB = base + ((first - s - 1 + MAX_SEGMENTS) % MAX_SEGMENTS);
        const ageA = time - this.pt[iA];
        const ageB = time - this.pt[iB];
        if (ageB > life) break;
        const tA = 1 - Math.min(1, ageA / life);
        const tB = 1 - Math.min(1, ageB / life);
        // AMD-7 R2a: width runs head → tail rather than head → nothing, so the ribbon
        // keeps a readable thread all the way back while still narrowing.
        const wA = wTail + wSpan * tA;
        const wB = wTail + wSpan * tB;
        // AMD-39: the taper is resolved twice per segment rather than six times per
        // segment in `emit`, which is what pays for doubling the segment budget.
        let rA = tA < prof.fadeFrac ? tA * invFade : 1;
        let rB = tB < prof.fadeFrac ? tB * invFade : 1;
        if (sq) {
          rA *= rA;
          rB *= rB;
        }
        const aA = aHead * rA * (aFloor + aSpan * tA);
        const aB = aHead * rB * (aFloor + aSpan * tB);
        const ax = headX + wrapDeltaX(this.px[iA] - headX);
        const ay = headY + wrapDeltaY(this.py[iA] - headY);
        const bx = headX + wrapDeltaX(this.px[iB] - headX);
        const by = headY + wrapDeltaY(this.py[iB] - headY);
        let dx = bx - ax;
        let dy = by - ay;
        const len = Math.hypot(dx, dy);
        if (len < 1e-5) continue;
        dx /= len;
        dy /= len;
        const nxA = -dy * hw * wA;
        const nyA = dx * hw * wA;
        const nxB = -dy * hw * wB;
        const nyB = dx * hw * wB;
        if (v + 6 > maxVerts) break;

        this.emit(pos, col, v++, ax + nxA, ay + nyA, r, g, b, aA);
        this.emit(pos, col, v++, ax - nxA, ay - nyA, r, g, b, aA);
        this.emit(pos, col, v++, bx - nxB, by - nyB, r, g, b, aB);
        this.emit(pos, col, v++, ax + nxA, ay + nyA, r, g, b, aA);
        this.emit(pos, col, v++, bx - nxB, by - nyB, r, g, b, aB);
        this.emit(pos, col, v++, bx + nxB, by + nyB, r, g, b, aB);
      }
    }

    this.vertexCount = v;
    this.geometry.setDrawRange(0, v);
    this.mesh.visible = v > 0;
    if (v === 0) return;
    this.posAttr.clearUpdateRanges();
    this.posAttr.addUpdateRange(0, v * 3);
    this.posAttr.needsUpdate = true;
    this.colAttr.clearUpdateRanges();
    this.colAttr.addUpdateRange(0, v * 4);
    this.colAttr.needsUpdate = true;
  }

  private emit(
    pos: Float32Array,
    col: Float32Array,
    v: number,
    x: number,
    y: number,
    r: number,
    g: number,
    b: number,
    a: number,
  ): void {
    pos[v * 3] = x;
    pos[v * 3 + 1] = y;
    pos[v * 3 + 2] = 0;
    col[v * 4] = r * 1.6;
    col[v * 4 + 1] = g * 1.6;
    col[v * 4 + 2] = b * 1.6;
    col[v * 4 + 3] = a;
  }

  get vertices(): number {
    return this.vertexCount;
  }

  clear(): void {
    for (let i = 0; i < this.slotOf.length; i++) this.slotOf[i] = -1;
    this.ownerSlotOf.fill(-1);
    this.len.fill(0);
    this.head.fill(0);
    this.owner.fill(0);
    this.freeTop = this.capacity;
    for (let i = 0; i < this.capacity; i++) this.free[i] = this.capacity - 1 - i;
    this.geometry.setDrawRange(0, 0);
    this.mesh.visible = false;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

/** §14.2: one merged ribbon buffer for friendly trails, one for enemy trails. */
export class TrailSystem {
  readonly friendly: TrailBuffer;
  readonly enemy: TrailBuffer;

  constructor(scene: THREE.Scene, projectileCapacity: number, enemyCapacity: number) {
    // The player rides the friendly buffer at owner slot `projectileCapacity`.
    // AMD-6 D6/D10 widened both: every friendly projectile now carries a ribbon, and
    // every moving swarm unit carries a 90 ms one.
    this.friendly = new TrailBuffer(320, projectileCapacity + 1, LAYER_TRAIL, 'trails-friendly');
    this.enemy = new TrailBuffer(256, enemyCapacity, LAYER_TRAIL + 1, 'trails-enemy');
    scene.add(this.friendly.mesh);
    scene.add(this.enemy.mesh);
  }

  build(time: number, camX: number, camY: number): void {
    this.friendly.build(time, camX, camY);
    this.enemy.build(time, camX, camY);
  }

  get vertices(): number {
    return this.friendly.vertices + this.enemy.vertices;
  }

  clear(): void {
    this.friendly.clear();
    this.enemy.clear();
  }

  dispose(): void {
    this.friendly.dispose();
    this.enemy.dispose();
  }
}
